import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotesStore, useUIStore } from '../store';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Separator } from './ui/separator';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger
} from './ui/context-menu';
import {
  Search,
  PanelLeftClose,
  PanelLeftOpen,
  Brain,
  LayoutGrid,
  Edit3,
  Eye,
  Columns
} from 'lucide-react';

export default function TopBar() {
  const navigate = useNavigate();
  const { searchQuery, searchResults, search, clearSearch } = useNotesStore();
  const { sidebarOpen, toggleSidebar, editorMode, setEditorMode, toggleAiPanel, aiPanelOpen } = useUIStore();
  const [showSearch, setShowSearch] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowSearch(true);
      }
      if (e.key === 'Escape') {
        setShowSearch(false);
        clearSearch();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const handleSearch = async (e) => {
    await search(e.target.value);
  };

  const handleSelectResult = (noteId) => {
    navigate(`/note/${noteId}`);
    setShowSearch(false);
    clearSearch();
  };

  return (
    <>
      <ContextMenu>
        <ContextMenuTrigger asChild>
          <header className="h-12 border-b bg-card flex items-center px-4 gap-4">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={toggleSidebar}>
              {sidebarOpen ? <PanelLeftClose size={18} /> : <PanelLeftOpen size={18} />}
            </Button>

            <button
              onClick={() => setShowSearch(true)}
              className="flex items-center gap-2 px-3 py-1.5 bg-muted border rounded-md text-muted-foreground hover:bg-muted/80 transition-colors flex-1 max-w-md text-sm"
            >
              <Search size={14} />
              <span>Search notes...</span>
              <kbd className="ml-auto text-xs bg-background px-1.5 py-0.5 rounded border">⌘K</kbd>
            </button>

            <div className="flex-1" />

            {/* Editor mode toggle */}
            <div className="flex items-center bg-muted rounded-md p-0.5">
              <Button
                variant={editorMode === 'edit' ? 'secondary' : 'ghost'}
                size="icon"
                className="h-7 w-7"
                onClick={() => setEditorMode('edit')}
              >
                <Edit3 size={14} />
              </Button>
              <Button
                variant={editorMode === 'split' ? 'secondary' : 'ghost'}
                size="icon"
                className="h-7 w-7"
                onClick={() => setEditorMode('split')}
              >
                <Columns size={14} />
              </Button>
              <Button
                variant={editorMode === 'preview' ? 'secondary' : 'ghost'}
                size="icon"
                className="h-7 w-7"
                onClick={() => setEditorMode('preview')}
              >
                <Eye size={14} />
              </Button>
            </div>

            <Separator orientation="vertical" className="h-6" />

            <Button
              variant={aiPanelOpen ? 'secondary' : 'ghost'}
              size="icon"
              className="h-8 w-8"
              onClick={toggleAiPanel}
            >
              <Brain size={16} />
            </Button>

            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => navigate('/graph')}
            >
              <LayoutGrid size={16} />
            </Button>
          </header>
        </ContextMenuTrigger>

        <ContextMenuContent className="w-48">
          <ContextMenuItem onClick={() => toggleSidebar()}>
            {sidebarOpen ? 'Hide Sidebar' : 'Show Sidebar'}
          </ContextMenuItem>
          <ContextMenuItem onClick={() => setEditorMode('edit')}>Edit Mode</ContextMenuItem>
          <ContextMenuItem onClick={() => setEditorMode('split')}>Split Mode</ContextMenuItem>
          <ContextMenuItem onClick={() => setEditorMode('preview')}>Preview Mode</ContextMenuItem>
          <ContextMenuSeparator />
          <ContextMenuItem onClick={() => toggleAiPanel()}>Toggle AI Panel</ContextMenuItem>
          <ContextMenuItem onClick={() => navigate('/graph')}>Open Graph View</ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>

      {/* Search modal */}
      {showSearch && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-[20vh]">
          <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={() => { setShowSearch(false); clearSearch(); }} />
          <div className="relative w-full max-w-2xl bg-popover border rounded-xl shadow-xl overflow-hidden animate-in fade-in zoom-in-95">
            <div className="flex items-center gap-3 px-4 py-3 border-b">
              <Search size={18} className="text-muted-foreground" />
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearch}
                placeholder="Search notes..."
                className="flex-1 bg-transparent text-lg focus:outline-none"
                autoFocus
              />
              <kbd className="text-xs bg-muted px-2 py-1 rounded border">ESC</kbd>
            </div>

            {searchResults.length > 0 && (
              <div className="max-h-96 overflow-y-auto p-2">
                {searchResults.map(result => (
                  <button
                    key={result.id}
                    onClick={() => handleSelectResult(result.id)}
                    className="w-full px-4 py-3 text-left rounded-lg hover:bg-muted transition-colors"
                  >
                    <div className="font-medium">{result.title}</div>
                    {result.snippet && (
                      <div className="text-sm text-muted-foreground mt-1 line-clamp-2" dangerouslySetInnerHTML={{ __html: result.snippet }} />
                    )}
                    {result.tags?.length > 0 && (
                      <div className="flex gap-1 mt-2">
                        {result.tags.map(tag => (
                          <span key={tag} className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}

            {searchQuery && searchResults.length === 0 && (
              <div className="px-4 py-8 text-center text-muted-foreground">No results found</div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
